import { useEffect, useRef, useState } from "react";
import { AssetIcon } from "../../assets/Icon.js";
import { pick, useT } from "../../i18n/index.js";
import { navigate } from "../../router.js";
import { useEvents } from "../../events/state.js";
import { orderedEventRuns } from "../../events/presentation.js";
import { useStore } from "../../store/state.js";
import { vipApi } from "../../store/api.js";
import { BundleList, PurchaseFx } from "../../store/cards.js";
import type { Bundle } from "../../store/types.js";
import { Button } from "../../ui/Button.js";
import { Icon } from "../../ui/Icon.js";
import { Timer, useServerTick } from "../../ui/Timer.js";
import { AutoCollectorControl } from "../AutoCollectorControl.js";

const SERVICE = {
  "bld.event_plaza": { eyebrow: { de: "Einsatzhof", en: "Operations yard" }, description: { de: "Aufträge, Events und deine Belohnungen.", en: "Missions, events and your rewards." }, route: "/app/events", label: "base.openEvents" },
  "bld.store_terminal": { eyebrow: { de: "Versorgungsdepot", en: "Supply depot" }, description: { de: "Nachschub für Basis und Trupps.", en: "Supplies for your base and squads." }, route: "/app/store", label: "base.openStore" },
  "bld.vip_lounge": { eyebrow: { de: "Offiziersquartier", en: "Officers' quarters" }, description: { de: "VIP-Vorteile und tägliche Versorgung.", en: "VIP benefits and daily supplies." }, route: "/app/store/vip", label: "base.openVip" },
} as const;
export function isServiceBuilding(id: string): id is keyof typeof SERVICE { return id in SERVICE; }

function Destination({ art, label, route, go }: { art: string; label: string; route: string; go: (route: string) => void }) {
  return <button type="button" className="service-route" onClick={() => go(route)} data-service-route={route}>
    <AssetIcon id={art} size={86} /><strong>{label}</strong><Icon name="chevronRight" />
  </button>;
}

/** Three distinct service entrances, with server-backed readiness and direct destinations. */
export function ServiceBuildingPanel({ id, level, close }: { id: keyof typeof SERVICE; level: number; close: () => void }) {
  const t = useT(), now = useServerTick();
  const definition = SERVICE[id];
  const runs = useEvents(s => s.runs), refreshRuns = useEvents(s => s.refreshRuns);
  const vip = useStore(s => s.vip), refreshVip = useStore(s => s.refreshVip);
  const [claiming, setClaiming] = useState(false), claimLock = useRef(false);
  const [received, setReceived] = useState<Bundle | null>(null);
  useEffect(() => {
    if (id === "bld.event_plaza") void refreshRuns();
    if (id === "bld.vip_lounge") void refreshVip();
  }, [id, refreshRuns, refreshVip]);
  const active = orderedEventRuns((runs ?? []).filter(run => run.startsAt <= now && run.endsAt > now && run.category !== "daily"), now);
  const go = (route: string) => { close(); navigate(route); };
  const claim = async () => {
    if (claimLock.current || !vip?.chest.claimable) return;
    claimLock.current = true; setClaiming(true);
    try {
      const result = await useStore.getState().run(() => vipApi.claimChest(), t("store.vip.chestClaimed"));
      if (result) {
        setReceived(result.granted);
        useStore.getState().celebrate({ key: "vip.chest", granted: result.granted, doubled: false, vipPoints: 0, nextStep: null });
        await refreshVip();
      }
    } finally { claimLock.current = false; setClaiming(false); }
  };
  return <div className="service-building" data-service-building={id}>
    <div className="service-hero">
      <AssetIcon id={id} buildingLevel={level} size={240} />
      <div className="service-identity"><span>{pick(definition.eyebrow)}</span><p>{pick(definition.description)}</p>
        {id === "bld.event_plaza" && runs ? <strong>{pick({ de: `${active.length} Events aktiv`, en: `${active.length} active events` })}</strong> : null}
        {id === "bld.vip_lounge" && vip ? <strong data-testid="service-vip-level">VIP {vip.level}{vip.megaRank > 0 ? ` · Mega ${vip.megaRank}` : ""}</strong> : null}
      </div>
    </div>
    <Button variant="gold" className="building-destination" data-testid="building-destination" onClick={() => go(definition.route)}>{t(definition.label)} <Icon name="arrowRight" /></Button>
    {id === "bld.event_plaza" ? <>
      {active[0] ? <div className="service-live-event"><span>{pick(active[0].name)}</span><Timer finishesAt={active[0].endsAt} /></div> : null}
      <div className="service-routes">
        <Destination art="itm.chest_gold" label={pick({ de: "Tagesbelohnungen", en: "Daily rewards" })} route="/app/events/daily" go={go} />
        <Destination art="ui.event.book" label={pick({ de: "Eventkalender", en: "Event calendar" })} route="/app/events/calendar" go={go} />
        <Destination art="zmb.brute" label="NEXUS · HQ 3" route="/app/nexus" go={go} />
      </div>
    </> : id === "bld.store_terminal" ? <div className="service-routes">
      <Destination art="bld.hydro_farm" label={pick({ de: "Ressourcen", en: "Resources" })} route="/app/store/resources" go={go} />
      <Destination art="bld.workshop" label={pick({ de: "Fabrikation", en: "Fabrication" })} route="/app/store/fabrication" go={go} />
    </div> : <>
      <div className="service-vip-chest"><AssetIcon id="itm.chest_gold" size={90} /><div><strong>{t("store.vip.chest")}</strong>
        {vip?.chest.claimable ? <Button variant="gold" disabled={claiming} onClick={() => void claim()} data-testid="service-vip-claim">{t("common.claim")}</Button> : <span data-testid="service-vip-wait">{vip ? vip.level > 0 ? <Timer finishesAt={vip.chest.resetsAt} /> : t("store.vip.chestLocked") : t("common.loading")}</span>}
      </div></div>
      {received ? <div className="service-receipt" role="status" data-testid="service-vip-receipt"><strong>{pick({ de: "Erhalten", en: "Received" })}</strong><BundleList bundle={received} compact /></div> : null}
      <details className="service-collector"><summary>{t("base.autoCollector.title")} · VIP 3</summary><AutoCollectorControl /></details>
    </>}
    {id === "bld.vip_lounge" ? <PurchaseFx /> : null}
  </div>;
}
